/**
 * 屏 5「域名绑定」的状态派生(纯函数,零 React)。
 *
 * ★ 单源是 `DomainCard.overallStatus`(规格 §6 状态链);四步 stepper 与右栏 7 节状态链都从它读,
 *   不另存「当前第几步」。`error` 落在哪一步,看三个子状态里哪一个没过(见 `failedStep`)。
 * ★ 子状态取值按规格 §1.3:verification pending / verified / failed;dns pending / ok / mismatch;
 *   ssl pending / issued / failed。api 桩或旧数据里可能出现 `checking`,当成「还没过」,不当失败。
 */
import type {
  DomainCard,
  DomainOverallStatus,
  DomainOverview,
  HostCert,
  PublisherMode,
  SiteDetail,
} from "../api/types";

/**
 * 输入框的格式闸,与 api `WebsiteDomainService.DOMAIN_RE` 同一条规则(归一化之后再比):总长 4–253;每段 1–63 个
 * `[a-z0-9-]`,不以 `-` 开头或结尾;顶级段是 2–63 个字母,或 punycode 顶级(`xn--…`,如 `xn--fiqs8s` = .中国)。
 * ★ api 那条用了后行断言 `(?<!-)`;这里改写成等价的「首尾必须是字母数字」,不依赖浏览器的后行断言支持。
 * ★ 只过这条还不够:整串是多段后缀(`com.cn`)的也要拒 —— 用 {@link isValidDomain}。
 */
export const DOMAIN_RE =
  /^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{0,58}[a-z0-9])$/;

/**
 * 按两段算会算错主域的常见多段后缀(api `ZoneNames.MULTI_PART`,附录 A.37)。整串就是其中之一 = 不是谁的域名,
 * api 拒(`ZoneNames.isBareSuffix`)。
 */
export const MULTI_PART_SUFFIXES: ReadonlySet<string> = new Set([
  "com.cn",
  "net.cn",
  "org.cn",
  "gov.cn",
  "edu.cn",
  "ac.cn",
  "com.hk",
  "co.uk",
]);

/**
 * 绑 www 还是裸域的提示(填写域名表单 / 自有域名卡第 1 步同一句):api 没有 A 记录 / 301(网关 IP 未定,规格 §9.1),
 * 裸域只能靠解析商的 CNAME 拉平(ALIAS),与 api `DomainCardAssembler` 给裸域 CNAME 行的备注同口径。
 */
export const APEX_HINT = "建议绑 www 二级域名;裸域要 DNS 服务商支持 CNAME 拉平(ALIAS)";

/** 归一化之后的主机名能不能绑(与 api `bind` 的格式闸同口径:正则 + 不是裸的多段后缀)。 */
export function isValidDomain(host: string): boolean {
  return DOMAIN_RE.test(host) && !MULTI_PART_SUFFIXES.has(host);
}

/** 含非 ASCII(中文域名 / 全角句号「。」)的主机名转 punycode,口径同 api `HostNames.normalize` 的 `IDN.toASCII`。 */
function toAsciiHost(host: string): string {
  if (!/[\u0080-\uffff]/.test(host)) return host;
  // 带端口 / 账号 / 空白的不是裸主机名:原样交给正则拒(URL 解析会把端口悄悄剥掉,那就替用户猜了)。
  if (/[\s:@\\/?#]/.test(host)) return host;
  try {
    return new URL(`http://${host}`).hostname;
  } catch {
    return host; // 非法 IDN:原样交给格式闸拒掉(同 api)
  }
}

/**
 * 用户贴进来的「域名」先剥成裸主机名再过格式闸:去首尾空白 → 去 `http://` / `https://` → 去路径 / 查询 / 锚点
 * (第一个 `/` `?` `#` 起)→ 去末尾的点(`www.a.com.` 是 FQDN 写法)→ 中文域名转 punycode → 小写。
 * 从浏览器地址栏整段复制(`https://www.a.com/`)是最常见的输入,直接报「格式不对」只会让人困惑。
 * ★ 与 api `HostNames.normalize` 同口径(trim / 去尾点 / IDN → punycode / 小写),前端先挡的和 api 最终认的是同一个串。
 */
export function normalizeDomainInput(raw: string): string {
  const host = raw
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/[/?#].*$/, "")
    .replace(/[.\u3002\uff0e\uff61]+$/, "")
    .trim();
  return toAsciiHost(host).replace(/\.+$/, "").toLowerCase();
}

/**
 * 模拟发布(发布服务器未接入,网关 `publisherMode === "stub"`,附录 A.38):stub 网关不签证书、域名检测到 CNAME 对了就
 * 直通 live —— 这种时候页面上**任何「证书 / HTTPS」字样都不出现**,改说「(模拟发布)」。下面几个 `simulated` 参数都是这个意思。
 * ★ 信号见 {@link isSimulatedPublish}:先看域名总览的 `publisherMode`(网关当前模式,A.40③),老后端没给再退到站点的
 *   `publisherMode`(当前线上版本走的网关)。
 */
export const SIMULATED_NOTE = "(模拟发布)";

/**
 * 域名页是不是模拟发布(附录 A.40③):`overview.publisherMode`(网关当前模式,草稿站也有)优先,没有才退到
 * `site.publisherMode`(当前线上版本走的网关,没发布过为 null → 按真实发布说话)。
 */
export function isSimulatedPublish(
  overview: { publisherMode?: PublisherMode | null } | null | undefined,
  site: { publisherMode?: PublisherMode | null } | null | undefined
): boolean {
  return (overview?.publisherMode ?? site?.publisherMode ?? null) === "stub";
}

/** 域名页是不是 host 模式(A.54 / A.57):网关当前模式优先,老后端没给退到站点的。 */
export function isHostDomain(
  overview: { publisherMode?: PublisherMode | null } | null | undefined,
  site: { publisherMode?: PublisherMode | null } | null | undefined
): boolean {
  return (overview?.publisherMode ?? site?.publisherMode ?? null) === "host";
}

/** 域名页「预览地址」卡画什么(A.51 / A.54 / 0924 E2E 走查)。 */
export interface PreviewAddress {
  /**
   * `custom` = 已上线的自有域名;`host-site` = 宿主 `?siteId=` 地址(泛解析未上线前唯一能开的);
   * `subdomain` = 平台预览子域(stub / remote,或宿主开了 `subdomain-preview`);`pending` = host 站还没发布,没有能开的地址。
   */
  kind: "custom" | "host-site" | "subdomain" | "pending";
  /** 卡上大字(不带 https://);`pending` 为 null。 */
  address: string | null;
  /** 复制 / 打开用的完整地址;`pending` 为 null。 */
  url: string | null;
}

/** 完整 url 的主机名(解析不了回 null)。 */
function urlHost(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).host.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * 预览地址三态(host 模式):
 *  - 自有域名已上线(`overallStatus === "live"`)→ 显示自有域名;
 *  - 已发布 → api 的 `previewUrl`:泛解析没开时是宿主 `https://site.xbwhynot.com/?siteId=…`,照它显示,
 *    **不显示** `{slug}.site.xbwhynot.com` 这个还解析不到的子域;api 开了 `subdomain-preview` 时 previewUrl 的主机就是
 *    `previewDomain`,那才显示子域;
 *  - 没发布过(宿主上还没有这个站,api 的 previewUrl 只是子域兜底)→ 不给地址,卡上写「发布后生成」。
 * 非 host 模式(stub / remote)行为不变:显示 `previewDomain`。
 */
export function previewAddress(
  overview: Pick<DomainOverview, "previewDomain" | "previewUrl" | "publisherMode">,
  site: Pick<SiteDetail, "status" | "previewUrl" | "publisherMode">,
  custom: Pick<DomainCard, "domain" | "overallStatus"> | null
): PreviewAddress {
  const url = overview.previewUrl ?? site.previewUrl;
  if (!isHostDomain(overview, site)) {
    return { kind: "subdomain", address: overview.previewDomain, url };
  }
  if (custom && custom.overallStatus === "live") {
    return { kind: "custom", address: custom.domain, url: `https://${custom.domain}` };
  }
  if (site.status !== "published" || !url) {
    return { kind: "pending", address: null, url: null };
  }
  if (urlHost(url) === overview.previewDomain.toLowerCase()) {
    return { kind: "subdomain", address: overview.previewDomain, url };
  }
  return { kind: "host-site", address: url.replace(/^https?:\/\//i, ""), url };
}

/** host 模式顶部提示(A.54:站点在大陆节点,自有域名要备案并接入阿里云)。 */
export const HOST_ICP_NOTE = "站点托管于大陆节点,自有域名需已备案并接入阿里云";

/**
 * 宿主证书签发状态 → 人话(A.54),挂在状态链「签发证书」那一节下面。
 * `failed` 显示检测备注原话(api 写进 `lastCheckNote`);`issued` 已上线不另说;没有值回 null。
 */
export function hostCertText(
  cert: HostCert | null | undefined,
  note?: string | null
): string | null {
  switch (cert) {
    case "pending":
      return "等待宿主签发证书";
    case "none":
      return "宿主尚未开始签发证书";
    case "failed":
      return note?.trim() ? note : "宿主签发证书失败";
    case "unknown":
      return "宿主签发状态接口未就绪";
    default:
      return null;
  }
}

/** 页头副标 / 自有域名卡徽标上的短话(设计稿「自有域名 www.… 解析检测中」)。 */
export const DOMAIN_STATUS_LABEL: Record<DomainOverallStatus, string> = {
  unbound: "未绑定",
  dns_pending: "待配置",
  verifying: "验证中",
  verified: "已验证",
  dns_checking: "解析检测中",
  issuing: "签发证书中",
  live: "已上线",
  error: "配置有误",
};

/** {@link DOMAIN_STATUS_LABEL},模拟发布时「签发证书中」换成「上线中」(A.38)。 */
export function domainStatusLabel(status: DomainOverallStatus, simulated = false): string {
  return simulated && status === "issuing" ? "上线中" : DOMAIN_STATUS_LABEL[status];
}

export type DomainTone = "ok" | "warn" | "error" | "muted";

export function domainStatusTone(status: DomainOverallStatus): DomainTone {
  if (status === "live") return "ok";
  if (status === "error") return "error";
  if (status === "unbound" || status === "dns_pending") return "muted";
  return "warn";
}

/** 右栏 7 节状态链(`error` 不是一节,它标红在出问题的那一节上)。 */
export const DOMAIN_CHAIN: ReadonlyArray<{ status: DomainOverallStatus; label: string }> = [
  { status: "unbound", label: "未绑定" },
  { status: "dns_pending", label: "待配置" },
  { status: "verifying", label: "验证中" },
  { status: "verified", label: "已验证" },
  { status: "dns_checking", label: "解析检测中" },
  { status: "issuing", label: "签发证书" },
  { status: "live", label: "已上线" },
];

/** 模拟发布时状态链「签发证书」那一节的名字(A.38:不出现「证书」)。 */
export function domainChain(
  simulated = false
): ReadonlyArray<{ status: DomainOverallStatus; label: string }> {
  return simulated
    ? DOMAIN_CHAIN.map((n) => (n.status === "issuing" ? { ...n, label: "上线" } : n))
    : DOMAIN_CHAIN;
}

/** 自有域名四步(设计稿逐字)。 */
export const DOMAIN_STEPS = ["填写域名", "验证所有权", "配置解析", "证书签发 + 上线"] as const;

/** 四步的名字;模拟发布时第 4 步不说「证书签发」(A.38)。 */
export function domainSteps(simulated = false): readonly string[] {
  return simulated ? [...DOMAIN_STEPS.slice(0, 3), `上线${SIMULATED_NOTE}`] : DOMAIN_STEPS;
}
/** 四步里的第几步(1-based)。 */
export type DomainStepNo = 1 | 2 | 3 | 4;
export type StepState = "done" | "current" | "error" | "todo";

const FAILED = new Set(["failed", "mismatch"]);
const PASSED = new Set(["verified", "ok", "issued"]);

/**
 * `error` 卡落在哪一步:先看哪个子状态明确失败了(TXT → 验证所有权,CNAME → 配置解析,证书 → 第 4 步);
 * 都没标失败(如 api 解析超时直接置 error)就落在第一个还没通过的那一步。
 */
export function failedStep(
  card: Pick<DomainCard, "verificationStatus" | "dnsStatus" | "sslStatus">
): 2 | 3 | 4 {
  if (FAILED.has(card.verificationStatus)) return 2;
  if (FAILED.has(card.dnsStatus)) return 3;
  if (FAILED.has(card.sslStatus)) return 4;
  if (!PASSED.has(card.verificationStatus)) return 2;
  if (!PASSED.has(card.dnsStatus)) return 3;
  return 4;
}

type StatusCard = Pick<
  DomainCard,
  "overallStatus" | "verificationStatus" | "dnsStatus" | "sslStatus"
>;

/** 当前那一步(`live` 回 5 = 四步全过)。 */
function currentStep(card: StatusCard): DomainStepNo | 5 {
  switch (card.overallStatus) {
    case "unbound":
      return 1;
    case "dns_pending":
    case "verifying":
      return 2;
    case "verified":
    case "dns_checking":
      return 3;
    case "issuing":
      return 4;
    case "live":
      return 5;
    case "error":
      return failedStep(card);
  }
}

/** 四步各自点亮成什么:之前 done、当前 current(`error` 时是 error)、之后 todo。 */
export function stepStates(card: StatusCard): StepState[] {
  const cur = currentStep(card);
  const isError = card.overallStatus === "error";
  return DOMAIN_STEPS.map((_, i) => {
    const n = i + 1;
    if (n < cur) return "done";
    if (n === cur) return isError ? "error" : "current";
    return "todo";
  });
}

/** 出问题的那一步对应状态链上的哪一节:验证所有权 → 验证中,配置解析 → 解析检测中,证书 → 签发证书。 */
const FAILED_NODE: Record<2 | 3 | 4, DomainOverallStatus> = {
  2: "verifying",
  3: "dns_checking",
  4: "issuing",
};

/**
 * 状态链当前节的下标(0..6)与是否异常。没有自有域名 = 停在「未绑定」。
 * `live` 是终点:这一节算当前(高亮),前面全部走过。
 */
export function chainPosition(card: StatusCard | null): { index: number; error: boolean } {
  if (!card) return { index: 0, error: false };
  const status =
    card.overallStatus === "error" ? FAILED_NODE[failedStep(card)] : card.overallStatus;
  return {
    index: DOMAIN_CHAIN.findIndex((n) => n.status === status),
    error: card.overallStatus === "error",
  };
}

/**
 * 检测之后状态有没有往前走(决定 toast 说「检测完成 · 新状态」还是把 `lastCheckNote` 亮给用户)。
 * 进了 `error` 不算前进。
 */
export function statusAdvanced(prev: DomainOverallStatus, next: DomainOverallStatus): boolean {
  if (next === "error") return false;
  const rank = (s: DomainOverallStatus) => DOMAIN_CHAIN.findIndex((n) => n.status === s);
  return rank(next) > rank(prev);
}

/**
 * DNS 表「状态」列的人话 + 色调。已知取值之外的原样显示(中性色)—— 不认识的状态画成「已验证」
 * 或干脆不画,都比原样摆出来更误导。
 */
const RECORD_STATUS: Record<string, { label: string; tone: DomainTone }> = {
  verified: { label: "已验证", tone: "ok" },
  ok: { label: "已生效", tone: "ok" },
  issued: { label: "已签发", tone: "ok" },
  checking: { label: "检测中", tone: "warn" },
  pending: { label: "待配置", tone: "muted" },
  not_configured: { label: "未配置", tone: "muted" },
  failed: { label: "未通过", tone: "error" },
  mismatch: { label: "解析不符", tone: "error" },
};

export function recordStatus(status: string): { label: string; tone: DomainTone } {
  return RECORD_STATUS[status] ?? { label: status, tone: "muted" };
}
